/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import type { StringSchema } from "yup";
import type {
  ReferenciasFieldName,
  ReferenciasStepsNames,
  TabsConfigResponseFormConfigField,
  TabsConfigResponseFormConfigSolicitudCredito,
  TabsConfigResponseFormConfigStep,
} from "@interfaces/response";
import { useLazyGetTabsConfigQuery } from "@api/catalogs";
import { BOFU_TABS_CONFIG, BOFU_TABS_FORM_CODES } from "@helpers/constantes";

type TaxSystemTypeCode = "PM" | "PFAE" | "PF";

const enabledField = (required: boolean): TabsConfigResponseFormConfigField => ({
  enabled: true,
  required,
});

/** Campos compartidos entre clientes y proveedores (misma forma de fila). */
const REFERENCE_ROW_SHARED_FIELDS: Record<string, TabsConfigResponseFormConfigField> = {
  contact_name: enabledField(true),
  email: enabledField(true),
  // Controla en UI country_code, phone y phone_extension.
  phone_number: enabledField(true),
};

/**
 * Defaults locales mientras el API no entregue `config.clientes|proveedores.fields`
 * para `REFERENCIAS_PERSONALES`. `hasStepFields` prioriza el API sobre estos defaults.
 */
const DEFAULT_CLIENTES: TabsConfigResponseFormConfigStep = {
  code: "CLIENTES",
  fields: {
    cliente: enabledField(true),
    ...REFERENCE_ROW_SHARED_FIELDS,
    // Solo UI de ReferenciasPersonales (PF); clientes/proveedores no los renderizan.
    relationship_type: enabledField(false),
    address: enabledField(false),
  },
};

const DEFAULT_PROVEEDORES: TabsConfigResponseFormConfigStep = {
  code: "PROVEEDORES",
  fields: {
    proveedor: enabledField(true),
    ...REFERENCE_ROW_SHARED_FIELDS,
  },
};

const DEFAULT_STEPS_BY_TAX_SYSTEM: Record<
  TaxSystemTypeCode,
  Partial<Record<ReferenciasStepsNames, TabsConfigResponseFormConfigStep>>
> = {
  PM: {
    clientes: DEFAULT_CLIENTES,
    proveedores: DEFAULT_PROVEEDORES,
  },
  PFAE: {
    clientes: DEFAULT_CLIENTES,
    proveedores: DEFAULT_PROVEEDORES,
  },
  PF: {
    clientes: DEFAULT_CLIENTES,
    proveedores: DEFAULT_PROVEEDORES,
  },
};

const getDefaultStepConfig = (
  taxSystemType: TaxSystemTypeCode,
  step: ReferenciasStepsNames,
): TabsConfigResponseFormConfigStep | undefined => DEFAULT_STEPS_BY_TAX_SYSTEM[taxSystemType][step];

const getReferenciasStepConfig = (
  config: TabsConfigResponseFormConfigSolicitudCredito | undefined,
  step: ReferenciasStepsNames,
): TabsConfigResponseFormConfigStep | undefined => config?.[step];

const hasStepFields = (stepConfig: TabsConfigResponseFormConfigStep | undefined): boolean =>
  Boolean(stepConfig?.fields && Object.keys(stepConfig.fields).length > 0);

const isStepFieldVisible = (
  stepConfig: TabsConfigResponseFormConfigStep | undefined,
  field: ReferenciasFieldName,
): boolean => {
  if (!hasStepFields(stepConfig)) return true;
  return stepConfig?.fields?.[field]?.enabled ?? false;
};

const isStepFieldRequired = (
  stepConfig: TabsConfigResponseFormConfigStep | undefined,
  field: ReferenciasFieldName,
  fallback: boolean,
): boolean => {
  if (!isStepFieldVisible(stepConfig, field)) return false;
  const fieldConfig = stepConfig?.fields?.[field];
  if (fieldConfig?.required === undefined) return fallback;
  return fieldConfig.required;
};

/** Tab donde vive el form `REFERENCIAS_PERSONALES`: `TBEPF` en PF, `TBEMP` en PM/PFAE. */
const getReferenciasTabCode = (taxSystemType: TaxSystemTypeCode): string =>
  taxSystemType === "PF" ? BOFU_TABS_CONFIG.tab_representante_pf.code : BOFU_TABS_CONFIG.tab_empresa.code;

const extractReferenciasConfig = (
  tabsConfig: Array<{ tabs_bofu: { code: string }; forms?: Array<{ code: string; config?: unknown }> }> | undefined,
  tabCode: string,
): TabsConfigResponseFormConfigSolicitudCredito | undefined => {
  const tab = tabsConfig?.find((item) => item.tabs_bofu.code === tabCode);
  const referenciasForm = tab?.forms?.find((form) => form.code === BOFU_TABS_FORM_CODES.referencias_personales);
  return (referenciasForm?.config as TabsConfigResponseFormConfigSolicitudCredito | undefined) ?? undefined;
};

/**
 * Controla visibilidad / obligatoriedad de campos en referencias.
 * - `clientes` → ReferenciasClientes / ReferenciasClientesPfae / ReferenciasPersonales (PF)
 * - `proveedores` → ReferenciasProveedores / ReferenciasProveedoresPfae
 *
 * Consulta `tabs_bofu_config` al montar (`TBEMP` en PM/PFAE, `TBEPF` en PF).
 * Si el step trae `fields` del API, los usa; si no (vacío / error), cae a defaults por `tax_system_type`.
 *
 * Nota: en las pantallas la obligatoriedad por fila también depende de `index < minReference`;
 * este hook solo resuelve el flag de config del campo (`enabled` / `required`).
 */
export const useReferenciasForm = (step: ReferenciasStepsNames) => {
  const taxcode = useSelector((state: RootState) => state.register.application.type);
  const taxSystemType = useSelector((state: RootState) => state.register.application.tax_system_type);
  const tabCode = getReferenciasTabCode(taxSystemType);

  const [getTabsConfig, { data: tabsConfig, isFetching, isUninitialized, isError }] = useLazyGetTabsConfigQuery();

  useEffect(() => {
    if (!taxcode) return;
    getTabsConfig({ taxcode, code: tabCode });
  }, [taxcode, tabCode]);

  const apiConfig = extractReferenciasConfig(tabsConfig, tabCode);
  const apiStepConfig = getReferenciasStepConfig(apiConfig, step);
  const stepConfig = hasStepFields(apiStepConfig) ? apiStepConfig : getDefaultStepConfig(taxSystemType, step);

  const isVisible = (field: ReferenciasFieldName) => isStepFieldVisible(stepConfig, field);

  const isRequired = (field: ReferenciasFieldName, fallback: boolean) =>
    isStepFieldRequired(stepConfig, field, fallback);

  const requiredField = <T extends StringSchema>(
    schema: T,
    field: ReferenciasFieldName,
    fallback: boolean,
    message = "Requerido",
  ) => (isRequired(field, fallback) ? schema.required(message) : schema.notRequired());

  // Solo la primera resolución (éxito o error). No vuelve a bloquear en refetch con cache.
  const isConfigLoading = Boolean(taxcode) && !isError && tabsConfig === undefined && (isUninitialized || isFetching);

  return {
    isVisible,
    isRequired,
    requiredField,
    stepConfig,
    isConfigLoading,
  };
};

export default useReferenciasForm;
